/**
 * Tiny dependency-free renderers for the OMS assistant:
 *  - renderMarkdown(): a GitHub-flavoured-Markdown subset for assistant replies
 *  - renderData():     a tool-result payload -> HTML table
 * Everything is HTML-escaped first.
 */

const esc = (s) =>
    String(s).replace(/[&<>"]/g, (c) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;'}[c]))

// OMS GLI = <2-digit data identifier>-<zero-padded id>, e.g. 13-01234 (order), 19-00088 (item).
// Turn any that appear in output into a click target; chat-message.vue delegates the click
// to helpers.global_lookup(gli, true).
const linkifyGli = (html) =>
    html.replace(/\b(\d{2}[-.]\d{5,8})\b/g, '<a href="#" class="gli-link" data-gli="$1">$1</a>')

function inline(s) {
    let out = esc(s)
    out = out.replace(/`([^`]+)`/g, (_, c) => `<code>${c}</code>`)
    out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    out = out.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
    out = out.replace(/\[([^\]]+)]\((https?:[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
    return linkifyGli(out)
}

const splitRow = (line) =>
    line.replace(/^\s*\|?/, '').replace(/\|?\s*$/, '').split('|').map((c) => c.trim())

const isTableSep = (line) => !!line && /^\s*\|?[\s:|-]+\|?\s*$/.test(line) && line.includes('-')

export function renderMarkdown(src) {
    const lines = String(src || '').replace(/\r\n/g, '\n').split('\n')
    let html = ''
    let i = 0

    while (i < lines.length) {
        const line = lines[i]

        if (/^```/.test(line)) {
            const buf = []
            i++
            while (i < lines.length && !/^```/.test(lines[i])) buf.push(lines[i++])
            i++
            html += `<pre><code>${esc(buf.join('\n'))}</code></pre>`
            continue
        }
        if (!line.trim()) {
            i++
            continue
        }

        const h = line.match(/^(#{1,6})\s+(.*)$/)
        if (h) {
            const n = h[1].length
            html += `<h${n}>${inline(h[2])}</h${n}>`
            i++
            continue
        }

        if (line.includes('|') && isTableSep(lines[i + 1])) {
            const head = splitRow(line)
            i += 2
            const rows = []
            while (i < lines.length && lines[i].includes('|') && lines[i].trim()) rows.push(splitRow(lines[i++]))
            html +=
                `<table class="table table-sm table-bordered chat-md-table"><thead><tr>` +
                head.map((c) => `<th>${inline(c)}</th>`).join('') +
                `</tr></thead><tbody>` +
                rows.map((r) => `<tr>${head.map((_, k) => `<td>${inline(r[k] || '')}</td>`).join('')}</tr>`).join('') +
                `</tbody></table>`
            continue
        }

        if (/^\s*[-*]\s+/.test(line) || /^\s*\d+\.\s+/.test(line)) {
            const ordered = /^\s*\d+\.\s+/.test(line)
            const items = []
            while (i < lines.length && (/^\s*[-*]\s+/.test(lines[i]) || /^\s*\d+\.\s+/.test(lines[i]))) {
                items.push(lines[i].replace(/^\s*(?:[-*]|\d+\.)\s+/, ''))
                i++
            }
            const tag = ordered ? 'ol' : 'ul'
            html += `<${tag}>${items.map((t) => `<li>${inline(t)}</li>`).join('')}</${tag}>`
            continue
        }

        if (/^\s*>\s?/.test(line)) {
            const buf = []
            while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
                buf.push(lines[i].replace(/^\s*>\s?/, ''))
                i++
            }
            html += `<blockquote>${inline(buf.join(' '))}</blockquote>`
            continue
        }

        const buf = []
        while (
            i < lines.length &&
            lines[i].trim() &&
            !/^(#{1,6}\s|```|\s*[-*]\s|\s*\d+\.\s|\s*>\s?)/.test(lines[i]) &&
            !(lines[i].includes('|') && isTableSep(lines[i + 1]))
            ) {
            buf.push(lines[i++])
        }
        html += `<p>${inline(buf.join(' '))}</p>`
    }

    return html
}

const MAX_ROWS = 30
const MAX_COLS = 16

function cellText(v) {
    if (v === null || v === undefined) return ''
    if (typeof v === 'object') {
        if (Array.isArray(v)) return v.every((x) => x === null || typeof x !== 'object') ? v.join(', ') : `[${v.length} items]`
        const j = JSON.stringify(v)
        return j.length <= 80 ? j : `{${Object.keys(v).length} fields}`
    }
    return String(v)
}

export function renderData(data) {
    if (data && typeof data === 'object' && !Array.isArray(data) &&
        Object.keys(data).length === 1 && (data._note || data.note)) {
        return `<div class="text-muted font-italic small">${esc(data._note || data.note)}</div>`
    }

    if (Array.isArray(data)) {
        let rows = data
        let foot = ''
        const last = rows[rows.length - 1]
        if (typeof last === 'string' && /more rows omitted/.test(last)) {
            foot = last
            rows = rows.slice(0, -1)
        }
        if (!rows.length) return `<div class="text-muted font-italic small">no rows${foot ? ' · ' + esc(foot) : ''}</div>`

        if (rows.every((r) => r === null || typeof r !== 'object')) {
            return tableHtml(null, rows.map((r) => [cellText(r)]), foot)
        }
        const cols = []
        rows.forEach((r) => r && typeof r === 'object' && Object.keys(r).forEach((k) => cols.includes(k) || cols.push(k)))
        const shown = cols.slice(0, MAX_COLS)
        if (cols.length > shown.length) foot = `${cols.length - shown.length} more column(s) hidden${foot ? ' · ' + foot : ''}`
        const body = rows.slice(0, MAX_ROWS).map((r) => shown.map((c) => cellText(r && typeof r === 'object' ? r[c] : '')))
        if (rows.length > MAX_ROWS) foot = `${rows.length - MAX_ROWS} more row(s) not shown${foot ? ' · ' + foot : ''}`
        return tableHtml(shown, body, foot)
    }

    if (data && typeof data === 'object') {
        return tableHtml(null, Object.entries(data).map(([k, v]) => [k, cellText(v)]), '', true)
    }

    return `<div class="text-muted small">${esc(String(data))}</div>`
}

function tableHtml(head, rows, foot, kv) {
    const thead = head ? `<thead><tr>${head.map((c) => `<th>${esc(c)}</th>`).join('')}</tr></thead>` : ''
    const tbody = rows
        .map((r) => `<tr>${r.map((v, i) => `<td${kv && i === 0 ? ' class="text-muted font-weight-bold"' : ''} title="${esc(v)}">${linkifyGli(esc(v))}</td>`).join('')}</tr>`)
        .join('')
    return (
        `<div class="table-responsive"><table class="table table-sm table-bordered chat-data-table small mb-1">${thead}<tbody>${tbody}</tbody></table></div>` +
        (foot ? `<div class="text-muted font-italic small">${esc(foot)}</div>` : '')
    )
}
